import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, ExternalLink } from "lucide-react";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="text-sm whitespace-pre-wrap">{children}</CardContent>
    </Card>
  );
}

export default function CompetitorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["/competitors", id],
    queryFn: async () => (await api.get(`/competitors/${id}`)).data,
    enabled: !!id,
  });

  async function del() {
    if (!data || !confirm(`Delete competitor "${data.name}"?`)) return;
    await api.delete(`/competitors/${id}`);
    qc.invalidateQueries({ queryKey: ["/competitors"] });
    navigate("/competitors");
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;

  const threatVariant = (t: string) =>
    t === "CRITICAL" ? "destructive" :
    t === "HIGH"     ? "destructive" :
    t === "MEDIUM"   ? "default" : "secondary";

  return (
    <>
      <PageHeader
        title={data.name}
        description={
          <span className="flex items-center gap-2">
            <Badge variant={threatVariant(data.threatLevel) as any}>{data.threatLevel} threat</Badge>
            {data.website && (
              <a href={data.website} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                {new URL(data.website).hostname.replace(/^www\./, "")} <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {!data.isActive && <Badge variant="outline">Inactive</Badge>}
          </span>
        }
        actions={<Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4"/>Delete</Button>}
      />

      <div className="grid gap-4 p-6 md:grid-cols-2">
        {data.description && <Section title="Description">{data.description}</Section>}
        {data.strengths && <Section title="Their strengths">{data.strengths}</Section>}
        {data.weaknesses && <Section title="Their weaknesses">{data.weaknesses}</Section>}
        {data.ourAdvantages && <Section title="Our advantages">{data.ourAdvantages}</Section>}
        {data.ourDisadvantages && <Section title="Our disadvantages">{data.ourDisadvantages}</Section>}

        <Card className="md:col-span-2">
          <CardHeader><CardTitle className="text-base">Where they show up — Accounts ({data.accountLinks?.length ?? 0})</CardTitle></CardHeader>
          <CardContent>
            {data.accountLinks?.length === 0 && <p className="text-sm text-muted-foreground">Not linked to any accounts yet.</p>}
            <ul className="divide-y text-sm">
              {data.accountLinks?.map((l: any) => (
                <li key={l.id} className="flex items-center justify-between py-2">
                  <Link to={`/accounts/${l.account.id}`} className="font-medium hover:underline">{l.account.name}</Link>
                  <span className="text-xs text-muted-foreground">{l.account.industry ?? ""}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader><CardTitle className="text-base">Where they show up — Deals ({data.dealLinks?.length ?? 0})</CardTitle></CardHeader>
          <CardContent>
            {data.dealLinks?.length === 0 && <p className="text-sm text-muted-foreground">Not linked to any deals yet.</p>}
            <ul className="divide-y text-sm">
              {data.dealLinks?.map((l: any) => (
                <li key={l.id} className="flex items-center justify-between py-2">
                  <Link to={`/deals/${l.deal.id}`} className="font-medium hover:underline">{l.deal.name}</Link>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="secondary">{l.deal.stage}</Badge>
                    <span className="tabular-nums">${Number(l.deal.valueUsd).toLocaleString()}</span>
                    {l.outcome && <Badge variant={l.outcome === "won" ? "default" : "destructive"}>{l.outcome}</Badge>}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
